import {
  Box,
  Circle,
  Icon,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { CheckIcon, CloseIcon } from "@chakra-ui/icons";
import { AnimatePresence, motion } from "framer-motion";
import { useMemo, type CSSProperties } from "react";
import { loadingMessageToText } from "src/Manager/EventDetail/Nav/Marketing/util";
import type { PublishLoadOutcome } from "../publishPollingUtils";

/** Smooth ease-out for entrances */
const easeOutExpo: [number, number, number, number] = [0.16, 1, 0.3, 1];
/** Slightly softer ease for exits */
const easeInOutSoft: [number, number, number, number] = [0.4, 0, 0.2, 1];

export type { PublishLoadOutcome };

export type CampaignPublishProgressIndicatorProps = {
  executionId?: string | null;
  guidedPublishStatus: string | null;
  serverPublishStep: string | null;
  /** While not idle, shows success/error after load; parent clears after ~2.8s. */
  publishLoadOutcome: PublishLoadOutcome;
};

/**
 * Header publish state: spinner + step text while in flight, then brief success/error from `publishLoadOutcome`.
 */
export function CampaignPublishProgressIndicator({
  executionId,
  guidedPublishStatus,
  serverPublishStep,
  publishLoadOutcome,
}: CampaignPublishProgressIndicatorProps) {
  const showLoading = useMemo(
    () =>
      Boolean(executionId) ||
      Boolean(guidedPublishStatus) ||
      Boolean(serverPublishStep),
    [executionId, guidedPublishStatus, serverPublishStep]
  );

  const progressLabel = useMemo(
    () =>
      (guidedPublishStatus != null &&
      String(guidedPublishStatus).trim() !== ""
        ? loadingMessageToText(guidedPublishStatus) ?? guidedPublishStatus
        : null) ??
      (serverPublishStep != null && String(serverPublishStep).trim() !== ""
        ? loadingMessageToText(serverPublishStep) ?? serverPublishStep
        : null),
    [guidedPublishStatus, serverPublishStep]
  );

  const showError = !showLoading && publishLoadOutcome === "error";
  const showSuccess = !showLoading && publishLoadOutcome === "success";

  if (!showLoading && !showError && !showSuccess) return null;

  const slotStyle: CSSProperties = {
    position: "absolute",
    left: 0,
    top: 0,
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    transformOrigin: "left center",
    willChange: "transform, opacity, filter",
  };

  return (
    <Box
      position="relative"
      minH="22px"
      minW={{ base: "min(280px, 85vw)", sm: "260px", md: "300px" }}
      pl={{ base: 0, sm: 1 }}
      flexShrink={0}
    >
      <AnimatePresence initial={false}>
        {showLoading ? (
          <motion.div
            key="publish-loading"
            initial={{ opacity: 0, scale: 0.92, filter: "blur(4px)" }}
            animate={{
              opacity: 1,
              scale: 1,
              filter: "blur(0px)",
              transition: {
                duration: 0.4,
                ease: easeOutExpo,
              },
            }}
            exit={{
              opacity: 0,
              scale: 0.85,
              x: 6,
              filter: "blur(4px)",
              transition: {
                duration: 0.32,
                ease: easeInOutSoft,
              },
            }}
            style={{ ...slotStyle, zIndex: 1 }}
          >
            <Box flexShrink={0} lineHeight={0}>
              <Spinner
                size="sm"
                color="gray.400"
                thickness="2px"
                speed="0.85s"
              />
            </Box>
            <Text
              fontFamily="Work Sans"
              fontSize="xs"
              fontWeight={500}
              color="gray.500"
              letterSpacing="-0.03em"
              noOfLines={1}
              maxW={{ base: "140px", sm: "220px", md: "280px" }}
            >
              {progressLabel ?? "Publishing…"}
            </Text>
          </motion.div>
        ) : showError ? (
          <motion.div
            key="publish-error"
            role="alert"
            aria-label="Publish failed"
            initial={{ opacity: 0, scale: 0.92, x: -4, filter: "blur(4px)" }}
            animate={{
              opacity: 1,
              scale: 1,
              x: 0,
              filter: "blur(0px)",
              transition: {
                opacity: { duration: 0.28, ease: easeOutExpo, delay: 0 },
                filter: { duration: 0.28, ease: easeOutExpo, delay: 0 },
                x: { duration: 0.32, ease: easeOutExpo, delay: 0 },
                scale: {
                  type: "spring",
                  stiffness: 320,
                  damping: 20,
                  mass: 0.5,
                  delay: 0.02,
                },
              },
            }}
            exit={{
              opacity: 0,
              y: -8,
              scale: 0.96,
              transition: { duration: 0.32, ease: easeInOutSoft },
            }}
            style={{ ...slotStyle, zIndex: 2 }}
          >
            <motion.div
              initial={{ scale: 0, opacity: 0.5 }}
              animate={{
                scale: 1,
                opacity: 1,
                transition: {
                  type: "spring",
                  stiffness: 340,
                  damping: 20,
                  mass: 0.5,
                },
              }}
              style={{ lineHeight: 0, flexShrink: 0 }}
            >
              <Circle size="22px" bg="red.500" color="white">
                <motion.span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{
                    scale: 1,
                    opacity: 1,
                    transition: {
                      type: "spring",
                      stiffness: 520,
                      damping: 24,
                      delay: 0.08,
                    },
                  }}
                >
                  <Icon as={CloseIcon} boxSize="10px" color="white" />
                </motion.span>
              </Circle>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -4 }}
              animate={{
                opacity: 1,
                x: 0,
                transition: {
                  duration: 0.3,
                  ease: easeOutExpo,
                  delay: 0.12,
                },
              }}
            >
              <Text
                fontFamily="Work Sans"
                fontSize="xs"
                fontWeight={600}
                color="red.600"
                letterSpacing="-0.03em"
                noOfLines={2}
                maxW={{ base: "140px", sm: "220px", md: "280px" }}
              >
                Publish failed
              </Text>
            </motion.div>
          </motion.div>
        ) : (
          <motion.div
            key="publish-success"
            role="status"
            aria-label="Published"
            initial={{ opacity: 0, scale: 0.92, x: -4, filter: "blur(4px)" }}
            animate={{
              opacity: 1,
              scale: 1,
              x: 0,
              filter: "blur(0px)",
              transition: {
                opacity: { duration: 0.28, ease: easeOutExpo, delay: 0 },
                filter: { duration: 0.28, ease: easeOutExpo, delay: 0 },
                x: { duration: 0.32, ease: easeOutExpo, delay: 0 },
                scale: {
                  type: "spring",
                  stiffness: 320,
                  damping: 20,
                  mass: 0.5,
                  delay: 0.02,
                },
              },
            }}
            exit={{
              opacity: 0,
              y: -8,
              scale: 0.96,
              transition: { duration: 0.32, ease: easeInOutSoft },
            }}
            style={{ ...slotStyle, zIndex: 2 }}
          >
            <motion.div
              initial={{ scale: 0, opacity: 0.5 }}
              animate={{
                scale: 1,
                opacity: 1,
                transition: {
                  type: "spring",
                  stiffness: 340,
                  damping: 20,
                  mass: 0.5,
                },
              }}
              style={{ lineHeight: 0, flexShrink: 0 }}
            >
              <Circle size="22px" bg="green.500" color="white">
                <motion.span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{
                    scale: 1,
                    opacity: 1,
                    transition: {
                      type: "spring",
                      stiffness: 520,
                      damping: 24,
                      delay: 0.08,
                    },
                  }}
                >
                  <Icon as={CheckIcon} boxSize="11px" color="white" />
                </motion.span>
              </Circle>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -4 }}
              animate={{
                opacity: 1,
                x: 0,
                transition: {
                  duration: 0.3,
                  ease: easeOutExpo,
                  delay: 0.12,
                },
              }}
            >
              <Text
                fontFamily="Work Sans"
                fontSize="xs"
                fontWeight={600}
                color="green.600"
                letterSpacing="-0.03em"
              >
                Published
              </Text>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
}
